import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, ShoppingBag, Gift, Clock, Repeat, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { useCart } from "../context/CartContext";
import { useMenu } from "../context/MenuContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "rivaaz_rewards_phone";

export default function RewardsModal({ open, onClose }) {
  const [phone, setPhone] = useState(() => localStorage.getItem(STORE_KEY) || "");
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("rewards");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      return;
    }
    const saved = localStorage.getItem(STORE_KEY);
    if (saved && !data) lookup(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lookup = async (p) => {
    setLoading(true);
    setError(null);
    try {
      const [rewardsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/rewards/${encodeURIComponent(p)}`),
        axios.get(`${API}/customers/${encodeURIComponent(p)}/orders?limit=10`).catch(() => ({ data: [] })),
      ]);
      setData(rewardsRes.data);
      setOrders(ordersRes.data || []);
      localStorage.setItem(STORE_KEY, p);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't fetch rewards");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length < 10) {
      setError("Please enter a 10-digit phone number");
      return;
    }
    lookup(phone);
  };

  const reset = () => {
    setData(null);
    setOrders([]);
    setPhone("");
    setTab("rewards");
    localStorage.removeItem(STORE_KEY);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rewards-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-[#0E0E1A] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C9853E]" /> Rivaaz Rewards
          </DialogTitle>
          <DialogDescription className="text-[#57534E] leading-relaxed">
            Earn 1 point for every $1 you spend. Redeem points for cash off your order.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <form onSubmit={submit} className="mt-2" data-testid="rewards-lookup-form">
            <label className="block">
              <span className="text-xs font-semibold tracking-wide uppercase text-[#57534E] mb-1.5 block">
                Phone number
              </span>
              <input
                data-testid="rewards-phone-input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(403) 555-1234"
                autoFocus
                className="w-full bg-white border border-[#E7E5E4] rounded-xl px-4 py-3 focus:outline-none focus:border-[#C9853E]"
              />
            </label>
            {error && <p className="text-sm text-[#C9853E] mt-2">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="rewards-lookup-submit"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-full px-6 py-3 font-bold disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check my rewards"}
            </button>
            <p className="text-xs text-[#57534E] mt-3">
              No sign-up needed. Just order online with the same phone number to start earning.
            </p>
          </form>
        ) : (
          <>
            {/* Tabs */}
            <div className="mt-2 flex gap-2 p-1 bg-[#F5F0E8] rounded-full" data-testid="rewards-tabs">
              <TabButton active={tab === "rewards"} onClick={() => setTab("rewards")} testId="tab-rewards">
                <Sparkles className="w-3.5 h-3.5" /> Rewards
              </TabButton>
              <TabButton active={tab === "history"} onClick={() => setTab("history")} testId="tab-history">
                <Clock className="w-3.5 h-3.5" /> History
                {orders.length > 0 && (
                  <span className="ml-1 text-[10px] font-bold bg-[#0E0E1A] text-white rounded-full px-1.5 min-w-[16px] inline-flex items-center justify-center">
                    {orders.length}
                  </span>
                )}
              </TabButton>
            </div>

            {tab === "rewards" ? (
              <RewardsView data={data} onReset={reset} />
            ) : (
              <OrderHistory orders={orders} onClose={onClose} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
        active ? "bg-white text-[#0E0E1A] shadow-sm" : "text-[#57534E] hover:text-[#0E0E1A]"
      }`}
    >
      {children}
    </button>
  );
}

function RewardsView({ data, onReset }) {
  const isMember = data.total_orders > 0;
  const next = data.next_tier;
  const progress = next
    ? Math.min(100, Math.round((data.points / next.points) * 100))
    : 100;

  return (
    <div className="mt-4" data-testid="rewards-view">
      <div className="bg-gradient-to-br from-[#0E0E1A] via-[#1C1B2B] to-[#0E0E1A] rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="absolute top-3 right-3 opacity-20">
          <Sparkles className="w-16 h-16 text-[#E5B07D]" />
        </div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#E5B07D] font-bold">
          {isMember ? `Hi ${data.name?.split(" ")[0] || "there"}` : "Welcome"}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-display text-5xl font-extrabold" data-testid="rewards-points">
            {data.points}
          </span>
          <span className="text-white/70 text-sm">points</span>
        </div>
        <div className="mt-1 text-xs text-white/60">
          Lifetime · {data.lifetime_points} pts · {data.total_orders}{" "}
          {data.total_orders === 1 ? "order" : "orders"}
        </div>

        {next && (
          <div className="mt-4">
            <div className="flex items-baseline justify-between text-xs mb-1.5">
              <span className="text-white/70">
                {next.points - data.points} pts to {next.label}
              </span>
              <span className="text-[#E5B07D] font-bold">{progress}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#C9853E] to-[#E5B07D]"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="text-xs tracking-[0.2em] uppercase text-[#57534E] font-bold mb-3">
          Available rewards
        </div>
        {data.available_rewards.length === 0 ? (
          <p className="text-sm text-[#57534E]">
            Place an order to start earning points. Your first reward unlocks at 100 points.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="rewards-available-list">
            {data.available_rewards.map((r, i) => (
              <li
                key={i}
                data-testid={`reward-tier-${r.points}`}
                className={`flex items-center justify-between p-3 rounded-xl border ${
                  data.points >= r.points
                    ? "border-[#C9853E] bg-[#C9853E]/5"
                    : "border-[#E7E5E4] bg-white opacity-60"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-[#C9853E]" />
                  <span className="font-bold text-[#0E0E1A]">{r.label}</span>
                </div>
                <span className="text-xs font-bold text-[#57534E]">{r.points} pts</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-[#57534E] mt-3 leading-relaxed flex items-center gap-1.5">
          <ShoppingBag className="w-3 h-3" />
          We'll auto-apply your best reward at checkout — you can opt out any time.
        </p>
      </div>

      <button
        onClick={onReset}
        data-testid="rewards-switch-btn"
        className="mt-4 w-full text-xs text-[#57534E] hover:text-[#C9853E]"
      >
        Use a different phone number
      </button>
    </div>
  );
}

function OrderHistory({ orders, onClose }) {
  const { add, setOpen: setCartOpen } = useCart();
  const { categories } = useMenu();
  const navigate = useNavigate();
  const [reorderingId, setReorderingId] = useState(null);

  // Build a lookup map of id → {item, variantByName}
  const itemLookup = (() => {
    const map = {};
    categories.forEach((c) => {
      (c.items || []).forEach((it) => {
        const variants = {};
        (it.variants || []).forEach((v) => {
          variants[v.name] = v;
        });
        map[it.id] = { item: it, variants };
      });
    });
    return map;
  })();

  const doReorder = (order) => {
    setReorderingId(order.id);
    let added = 0;
    const skipped = [];
    order.items.forEach((oi) => {
      const lookup = itemLookup[oi.item_id];
      if (!lookup) {
        skipped.push(oi.name);
        return;
      }
      const { item, variants } = lookup;
      let variantName = oi.variant || null;
      let price = null;
      if (item.variants && item.variants.length > 0) {
        const v = variantName && variants[variantName];
        if (v) {
          price = v.price;
        } else {
          // Variant renamed/removed — pick first variant
          variantName = item.variants[0].name;
          price = item.variants[0].price;
        }
      } else if (item.price != null) {
        price = item.price;
        variantName = null;
      } else {
        skipped.push(oi.name);
        return;
      }
      add({
        item_id: item.id,
        variant_name: variantName,
        variant_label: variantName,
        name: item.name,
        price,
        quantity: oi.quantity,
      });
      added += oi.quantity;
    });
    setReorderingId(null);
    if (added === 0) {
      toast.error("Those items are no longer available");
      return;
    }
    if (skipped.length > 0) {
      toast.warning(`Added ${added} items. Skipped: ${skipped.join(", ")}`);
    } else {
      toast.success(`Re-added ${added} items to your cart`);
    }
    setCartOpen(true);
    onClose();
    navigate("/order");
  };

  if (orders.length === 0) {
    return (
      <div className="mt-6 text-center py-10" data-testid="no-orders">
        <Clock className="w-10 h-10 text-[#57534E] mx-auto mb-3 opacity-40" />
        <p className="text-sm text-[#57534E]">No orders yet on this number.</p>
        <p className="text-xs text-[#57534E] mt-1">Place your first order to start earning points.</p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3" data-testid="order-history-list">
      {orders.map((o) => {
        const date = new Date(o.created_at);
        const dateStr = date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
        });
        const itemCount = o.items.reduce((s, i) => s + i.quantity, 0);
        return (
          <div
            key={o.id}
            data-testid={`order-history-${o.id}`}
            className="border border-[#E7E5E4] rounded-2xl p-4 bg-white hover:border-[#C9853E]/60 transition-colors"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#C9853E] font-bold">
                  {dateStr} · #{o.id.slice(0, 6)}
                </div>
                <div className="font-display text-base font-bold text-[#0E0E1A] mt-0.5 leading-tight">
                  {itemCount} {itemCount === 1 ? "item" : "items"} · ${o.subtotal.toFixed(2)}
                </div>
              </div>
              <StatusPill status={o.status} />
            </div>

            <ul className="mt-2 text-xs text-[#57534E] space-y-0.5">
              {o.items.slice(0, 3).map((it, i) => (
                <li key={i}>
                  {it.quantity}× {it.name}
                  {it.variant && <span className="opacity-70"> — {it.variant}</span>}
                </li>
              ))}
              {o.items.length > 3 && (
                <li className="italic opacity-70">+ {o.items.length - 3} more</li>
              )}
            </ul>

            {o.points_earned > 0 && (
              <div className="mt-2 text-[11px] text-[#A6692D] inline-flex items-center gap-1 font-bold">
                <Sparkles className="w-3 h-3" /> +{o.points_earned} pts earned
                {o.points_redeemed > 0 && (
                  <span className="ml-1 text-[#57534E] font-normal">
                    · used {o.points_redeemed} for ${o.discount_amount.toFixed(2)} off
                  </span>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={() => doReorder(o)}
              disabled={reorderingId === o.id}
              data-testid={`reorder-btn-${o.id}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-[#0E0E1A] hover:bg-[#C9853E] disabled:opacity-50 text-white rounded-full px-4 py-2 text-xs font-bold transition-all"
            >
              {reorderingId === o.id ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Repeat className="w-3.5 h-3.5" />
              )}
              Order it again
            </button>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: { label: "New", bg: "bg-[#C9853E]/10", fg: "text-[#A6692D]" },
    preparing: { label: "Preparing", bg: "bg-[#C9853E]/10", fg: "text-[#A6692D]" },
    ready: { label: "Ready", bg: "bg-[#374F3B]/10", fg: "text-[#374F3B]" },
    completed: { label: "Completed", bg: "bg-[#374F3B]/10", fg: "text-[#374F3B]" },
    cancelled: { label: "Cancelled", bg: "bg-[#E7E5E4]", fg: "text-[#57534E]" },
  };
  const s = map[status] || map.new;
  return (
    <span className={`text-[9px] font-bold tracking-[0.15em] uppercase px-2 py-1 rounded-full ${s.bg} ${s.fg} inline-flex items-center gap-1 whitespace-nowrap`}>
      {status === "completed" && <Check className="w-2.5 h-2.5" />}
      {s.label}
    </span>
  );
}
